/**
 * React Query hook for fetching user tier data
 *
 * Part of Story 3.1: Tier Badge Component
 * Epic 3: Frontend UI Components
 *
 * Fetches tier information from backend API and provides
 * efficient caching and automatic refetching.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { TierData } from '@/types/billing';

/**
 * Fetches current user's tier data from backend API
 * Endpoint: GET /api/v1/tier/current
 *
 * Uses apiClient which includes authentication headers automatically.
 *
 * @returns Promise resolving to TierData
 * @throws Error if API request fails
 */
async function fetchTierData(): Promise<TierData> {
  const response = await apiClient.get<TierData>('/tier/current');
  return response.data;
}

/**
 * Custom hook for fetching and caching user tier data
 *
 * Uses React Query for:
 * - Automatic caching (5-minute stale time)
 * - Background refetching
 * - Loading and error states
 * - Request deduplication
 *
 * @returns React Query result with tier data, loading state, and error state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data: tierData, isLoading, isError } = useTierData();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (isError) return <Error />;
 *
 *   return <TierBadge tier={tierData.tier} />;
 * }
 * ```
 */
export function useTierData() {
  return useQuery<TierData, Error>({
    queryKey: ['tier', 'current'],
    queryFn: fetchTierData,

    // Tier data changes infrequently - consider data fresh for 5 minutes
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Keep data in cache for 10 minutes even if unused
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime in v4)

    // Don't refetch on window focus (tier rarely changes)
    refetchOnWindowFocus: false,

    // Retry once on failure
    retry: 1,

    // Show cached data while refetching in background
    refetchOnMount: 'always',
  });
}
